import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Home, Wallet, Receipt, Banknote, Settings, MoreHorizontal, FileText, ClipboardCheck, History, FileSpreadsheet, LogOut, ShieldCheck, Sun, Moon } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePeriod } from '@/context/PeriodContext';
import { useTheme } from '@/context/ThemeContext';
import { MONTH_NAMES } from '@/lib/format';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

const primaryNav = [
  { to: '/', label: 'Panel', icon: Home, tid: 'nav-home' },
  { to: '/ventas', label: 'Ventas', icon: Wallet, tid: 'nav-sales' },
  { to: '/gastos', label: 'Gastos', icon: Receipt, tid: 'nav-expenses' },
  { to: '/depositos', label: 'Depósitos', icon: Banknote, tid: 'nav-deposits' },
];

const moreNav = [
  { to: '/conciliacion', label: 'Conciliación', icon: ClipboardCheck, tid: 'more-conciliacion' },
  { to: '/preformulario', label: 'Preformulario', icon: FileText, tid: 'more-preform' },
  { to: '/reportes', label: 'Reportes', icon: FileSpreadsheet, tid: 'more-reports' },
  { to: '/historial', label: 'Historial', icon: History, tid: 'more-history' },
  { to: '/configuracion', label: 'Configuración fiscal', icon: Settings, tid: 'more-config' },
];

export default function AppLayout() {
  const { logout, user } = useAuth();
  const { year, month } = usePeriod();
  const { theme, toggle: toggleTheme } = useTheme();
  const [moreOpen, setMoreOpen] = React.useState(false);

  return (
    <div className="min-h-screen w-full flex justify-center bg-background">
      <div className="w-full max-w-md min-h-screen flex flex-col relative">
        <header data-testid="app-header" className="glass-nav sticky top-0 z-40 safe-top border-b border-border/60 px-5 pt-3 pb-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">REGIMEN SIMPLIFICADO DE CONFIANZA 626 · MEXICO <span className="text-[#F97316]">{"(BETA)"}</span></p>
              <h1 className="font-display text-xl font-extrabold tracking-tight leading-tight" data-testid="app-current-period">
                {MONTH_NAMES[month - 1]} <span className="text-muted-foreground font-medium">{year}</span>
              </h1>
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <button data-testid="header-account-btn" className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center border border-border">
                  <ShieldCheck className="h-5 w-5 text-primary" strokeWidth={2}/>
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85%] sm:w-96">
                <SheetHeader>
                  <SheetTitle className="font-display">Cuenta</SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-3">
                  <div className="rounded-lg border border-border p-4 bg-secondary/40">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Administrador</p>
                    <p className="font-medium mt-1" data-testid="account-email">{user?.email}</p>
                  </div>
                  <button
                    type="button"
                    data-testid="btn-toggle-theme"
                    onClick={toggleTheme}
                    className="w-full rounded-lg border border-border p-4 flex items-center justify-between hover:bg-secondary/40 active:scale-[0.99] transition-transform"
                  >
                    <div className="text-left">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Tema</p>
                      <p className="font-medium mt-1" data-testid="theme-label">{theme === 'dark' ? 'Oscuro' : 'Claro'}</p>
                    </div>
                    <span className="h-10 w-10 rounded-full bg-secondary border border-border flex items-center justify-center">
                      {theme === 'dark' ? <Sun className="h-5 w-5"/> : <Moon className="h-5 w-5"/>}
                    </span>
                  </button>
                  <Button variant="outline" className="w-full h-11 rounded-full" data-testid="btn-logout" onClick={logout}>
                    <LogOut className="h-4 w-4 mr-2"/> Cerrar sesión
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </header>

        <main className="flex-1 pb-nav px-5 pt-4">
          <Outlet />
        </main>

        <nav data-testid="bottom-nav" className="glass-nav fixed bottom-0 left-0 right-0 mx-auto max-w-md border-t border-border/60 safe-bottom z-40">
          <div className="grid grid-cols-5">
            {primaryNav.map(({ to, label, icon: Icon, tid }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                data-testid={tid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium tracking-wide ${isActive ? 'text-primary' : 'text-muted-foreground'}`
                }
              >
                <Icon className="h-[22px] w-[22px]" strokeWidth={2}/>
                <span>{label}</span>
              </NavLink>
            ))}
            <button
              data-testid="nav-more"
              onClick={() => setMoreOpen(true)}
              className="flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium tracking-wide text-muted-foreground"
            >
              <MoreHorizontal className="h-[22px] w-[22px]" strokeWidth={2}/>
              <span>Más</span>
            </button>
          </div>
        </nav>

        <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
          <SheetContent side="bottom" className="rounded-t-2xl">
            <SheetHeader>
              <SheetTitle className="font-display text-left">Más opciones</SheetTitle>
            </SheetHeader>
            <div className="grid grid-cols-2 gap-3 mt-4 pb-6">
              {moreNav.map(({ to, label, icon: Icon, tid }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={tid}
                  onClick={() => setMoreOpen(false)}
                  className="flex flex-col items-start gap-2 p-4 rounded-lg border border-border bg-card active:scale-95 transition-transform"
                >
                  <Icon className="h-5 w-5 text-primary" strokeWidth={2}/>
                  <span className="text-sm font-medium leading-tight">{label}</span>
                </NavLink>
              ))}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}
